import React, { useState } from "react";
import { Edit, useAutocomplete } from "@refinedev/mui";
import {
  Box,
  TextField,
  Autocomplete,
  FormControl,
  FormLabel,
  Slider,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { IResourceComponentsProps, useTranslate } from "@refinedev/core";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import EmojiPicker from "emoji-picker-react";
import { useAuth } from "../../contexts/auth-context";
import AddressAutocomplete, { GeoapifyResult } from "../../components/forms/AddressAutocomplete";

// Fix pour les icônes Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function LocationMarker({ position, setPosition, radius }: any) {
  const map = useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  React.useEffect(() => {
    if (position && map) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);

  return position === null ? null : (
    <>
      <Marker position={position} />
      <Circle center={position} radius={radius} />
    </>
  );
}


export const SharedPlacesEdit: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "ADMIN";

  const {
    saveButtonProps,
    refineCore: { formLoading, queryResult },
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    refineCoreProps: {
      resource: "places",
    },
  });

  const placeData = queryResult?.data?.data;

  const [position, setPosition] = useState<[number, number]>([
    placeData?.latitude || 48.8566,
    placeData?.longitude || 2.3522,
  ]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const radius = watch("radius") || placeData?.radius || 100;
  const selectedEmoji = watch("emoji") || placeData?.emoji || "📍";

  // Calculate zoom based on radius
  const zoom = React.useMemo(() => {
    return radius > 500 ? 13 : radius > 200 ? 14 : 15;
  }, [radius]);

  // Autocomplete pour les entreprises (seulement pour ADMIN)
  const { autocompleteProps: companyAutocompleteProps } = useAutocomplete({
    resource: "company",
    defaultValue: placeData?.entrepriseId,
  });

  React.useEffect(() => {
    if (placeData) {
      setPosition([placeData.latitude, placeData.longitude]);
    }
  }, [placeData]);

  React.useEffect(() => {
    setValue("latitude", position[0]);
    setValue("longitude", position[1]);
  }, [position, setValue]);

  return (
    <Edit isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("name", {
                    required: translate("sharedPlaces.form.nameRequired"),
                  })}
                  error={!!(errors as any)?.name}
                  helperText={(errors as any)?.name?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label={translate("sharedPlaces.fields.name")}
                  name="name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          sx={{
                            fontSize: "2rem",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.08)",
                            },
                          }}
                        >
                          {selectedEmoji}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />

            {showEmojiPicker && (
              <Box sx={{ mt: 2 }}>
                <EmojiPicker
                  onEmojiClick={(emojiData: any) => {
                    setValue("emoji", emojiData.emoji);
                    setShowEmojiPicker(false);
                  }}
                />
              </Box>
            )}

            <Box sx={{ mt: 2, mb: 2 }}>
              <FormLabel>{translate("sharedPlaces.form.notificationRadius")}</FormLabel>
              <Slider
                value={radius}
                onChange={(_, value) => setValue("radius", value as number)}
                min={50}
                max={1000}
                step={50}
                valueLabelDisplay="auto"
                marks={[
                  { value: 50, label: "50m" },
                  { value: 500, label: "500m" },
                  { value: 1000, label: "1km" },
                ]}
              />
            </Box>

            <Controller
              control={control}
              name="isActive"
              defaultValue={placeData?.isActive ?? true}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={translate("sharedPlaces.form.placeActive")}
                />
              )}
            />

            {isAdmin && (
              <Controller
                control={control}
                name="entrepriseId"
                defaultValue={placeData?.entrepriseId}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <Autocomplete
                      {...companyAutocompleteProps}
                      {...field}
                      onChange={(_, value: any) => {
                        field.onChange(value?.id || null);
                      }}
                      getOptionLabel={(item: any) => {
                        return item.name ? item.name : "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        value === undefined || option?.id?.toString() === value?.id?.toString()
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("sharedPlaces.form.companyOptional")}
                          variant="outlined"
                          helperText={translate("sharedPlaces.form.companyHelperText")}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            )}

                {!isAdmin && placeData?.entrepriseId && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                    <Typography variant="body2">
                      {translate("sharedPlaces.form.sharedWithCompany")}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ mt: 2 }}>
                  <AddressAutocomplete
                    label={translate("sharedPlaces.form.searchAddress")}
                    placeholder={translate("sharedPlaces.form.enterAddress")}
                    onAddressSelect={(result: GeoapifyResult) => {
                      setPosition([result.lat, result.lon]);
                      setValue("latitude", result.lat);
                      setValue("longitude", result.lon);
                      setValue("address", result.formatted || result.address_line1 || `${result.lat}, ${result.lon}`);
                    }}
                  />
                </Box>
                <Box sx={{ height: "500px", width: "100%", mt: 2, borderRadius: '6px', overflow: 'hidden' }}>
                  <MapContainer
                    center={position}
                    zoom={zoom}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker
                      position={position}
                      setPosition={setPosition}
                      radius={radius}
                    />
                  </MapContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <input type="hidden" {...register("latitude")} />
        <input type="hidden" {...register("longitude")} />
        <input type="hidden" {...register("address")} />
        <input type="hidden" {...register("emoji")} />
        <input type="hidden" {...register("radius")} />
      </Box>
    </Edit>
  );
};
